/**
 * Sidebar Icon Mappings
 *
 * This utility provides consistent icon mapping for collapsible sidebars
 * throughout the application, following the standards defined in:
 * docs/prds/PRD-UI-ARCHITECTURE-RULES.md § 3.7.5
 */

import {
  Home,
  Network,
  MapPin,
  DollarSign,
  Briefcase,
  FileText,
  Clock,
  Users,
  UsersRound,
  Building2,
  Building,
  Handshake,
  LayoutDashboard,
  type LucideIcon,
} from "lucide-react";

/**
 * Standard icon mappings for detail page navigation sections
 * Used in collapsed sidebar state to replace text labels
 */
const SECTION_ICON_MAP: Record<string, LucideIcon> = {
  Overview: LayoutDashboard,  // Entity overview (different from Dashboard/Home)
  Structure: Network,
  Addresses: MapPin,
  Financials: DollarSign,     // Financial data/money
  Facilities: Building,       // Financial facilities/properties
  Deals: Briefcase,           // Consistent with main nav - business deals/transactions
  Documents: FileText,
  Activity: Clock,
  Contacts: Users,            // Consistent with main nav - individual people
  Accounts: Building2,        // Consistent with main nav - business entities
  Partners: Handshake,        // Consistent with main nav - partnerships/collaboration
  // Add more standard mappings as needed
};

/**
 * Standard icon mappings for linked entity sections
 * Used in collapsed sidebar state to show entity counts with icons
 * Supports both singular and plural forms for flexibility
 */
const LINKED_ENTITY_ICON_MAP: Record<string, LucideIcon> = {
  // Contacts (plural and singular) - consistent with main nav (individual people)
  Contacts: Users,
  Contact: Users,

  // Accounts (plural and singular) - consistent with main nav (business entities)
  Accounts: Building2,
  Account: Building2,

  // Deals (plural and singular) - consistent with main nav (business deals/transactions)
  Deals: Briefcase,
  Deal: Briefcase,

  // Documents (plural and singular)
  Documents: FileText,
  Document: FileText,

  // Client Groups - consistent with main nav
  "Client Groups": UsersRound,
  "Client Group": UsersRound,

  // Partners - consistent with main nav (partnerships/collaboration)
  Partners: Handshake,
  Partner: Handshake,

  // Add more linked entity mappings as needed
};

/**
 * Get the appropriate icon component for a navigation section label
 *
 * @param sectionLabel - The section label (e.g., "Overview", "Structure")
 * @param customIcon - Optional custom icon to override default mapping
 * @returns The icon component to render (defaults to Home if no match found)
 *
 * @example
 * const IconComponent = getIconForSection("Overview");
 * return <IconComponent className="h-5 w-5" />;
 */
export function getIconForSection(
  sectionLabel: string,
  customIcon?: LucideIcon
): LucideIcon {
  if (customIcon) {
    return customIcon;
  }

  return SECTION_ICON_MAP[sectionLabel] || Home; // Default fallback to Home icon
}

/**
 * Get the appropriate icon component for a linked entity label
 *
 * @param entityLabel - The entity label (may include count, e.g., "Contacts (3)")
 * @param customIcon - Optional custom icon to override default mapping
 * @returns The icon component to render (defaults to Users if no match found)
 *
 * @example
 * const IconComponent = getIconForLinkedEntity("Contacts (3)");
 * return <IconComponent className="h-5 w-5" />;
 */
export function getIconForLinkedEntity(
  entityLabel: string,
  customIcon?: LucideIcon
): LucideIcon {
  if (customIcon) {
    return customIcon;
  }

  // Remove count from label if present (e.g., "Contacts (3)" -> "Contacts")
  // Also remove trailing arrows and other indicators (e.g., "Contacts →" -> "Contacts")
  const cleanLabel = entityLabel
    .replace(/\s*\(\d+\)\s*/g, '')
    .replace(/\s*→\s*/g, '')
    .trim();

  // Try exact match first
  if (LINKED_ENTITY_ICON_MAP[cleanLabel]) {
    return LINKED_ENTITY_ICON_MAP[cleanLabel];
  }

  // Try case-insensitive match
  const lowercaseLabel = cleanLabel.toLowerCase();
  for (const [key, icon] of Object.entries(LINKED_ENTITY_ICON_MAP)) {
    if (key.toLowerCase() === lowercaseLabel) {
      return icon;
    }
  }

  // Default fallback to Users icon
  return Users;
}

/**
 * Type for navigation items that support custom icons
 */
export type NavItemWithIcon = {
  label: string;
  href: string;
  count?: number;
  icon?: LucideIcon;
};

/**
 * Type for linked entities that support custom icons
 */
export type LinkedEntityWithIcon = {
  label: string;
  count: number;
  href?: string;
  icon?: LucideIcon;
};
